import { Component, OnInit, Input, ViewChild } from "@angular/core";
import { FirebaseService } from "../../services/firebase.service";
import { Logger } from "src/app/services/datadog.logger.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Guid } from "guid-typescript";
import { FormInputTypes } from "../../models/formtemplate";
import {
  Validators,
  FormGroup,
  FormBuilder,
  FormControl,
} from "@angular/forms";
import { Subject, Observable } from "rxjs";
import { WebcamImage, WebcamInitError, WebcamUtil } from "ngx-webcam";
import { Location } from "@angular/common";
import * as firebase from "firebase";

import "firebase/firestore";
import "firebase/auth";
import "firebase/storage";

interface LinkedListNode {
  formTemplate: any;
  next?: any;
  previous?: any;
  location?: number;
}

class ImageSnippet {
  constructor(public src: string, public file: File) {}
}

@Component({
  selector: "app-forms",
  templateUrl: "./forms.component.html",
  styleUrls: ["./forms.component.less", "../../../assets/style.less"],
})
export class FormsComponent implements OnInit {
  @ViewChild("clientForm") clientForm;
  clientFormGroup: FormGroup;
  submitted = false;
  saving = false;
  finished = false;
  // Data to be saved to Firebase

  /** Filled out form data.  The data is binded on each control. */
  formResponse = {};
  /** Filled out form data in reference to client */
  client = {};

  // Firebase objects retrieved from server
  formTemplateNode: LinkedListNode;

  formTemplate: any;

  /// Form templates are retrieved from the server and then linked together as a linked list
  formTemplateNodes: LinkedListNode[] = [];

  // The artist
  arist: any;

  // The tags within the template, so for example, if user.businessName is a tag than we need to replace that tag with the user's business name
  tags: [string];

  private trigger: Subject<void> = new Subject<void>();

  // latest snapshot
  public webcamImage: WebcamImage = null;
  // Displays whenever there's an error with the image, including if the user has not added an image yet
  imageError;
  // Displays at the bottom of the secret above the submit button letting them know there are errors above
  formError;

  // If the user is editing a form than this value should be set to the Id of the filled form being edited
  filledFormId: string;

  companyName: string;

  // Whether or not we should show the button that takes them back to the dashboard
  showDashboardButton: Boolean;

  // Whether or not we should allow the user to actually fill out this form
  viewOnly: string;

  // All the forms for all the selected services
  forms: any[];

  // The id for this specific appointment.  This is used to keep track of the user's forms so that they can leave this page and still come
  // back and fill out the rest of the forms
  appointmentId: string;

  finishedAllForms = false;

  filledOutForms = {};

  // Id of the artist that the forms and appointment are for
  artistId: string;

  todaysDate = new Date().toJSON().split("T")[0];

  cameraNotAvailable = true;

  // The image as a data url
  imageAsDataUrl: string | ArrayBuffer;

  // The url of a client image
  imageUrl: string;

  CURRENT_FORM_ID = "currentFormId";

  constructor(
    private service: FirebaseService,
    private logger: Logger,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private location: Location
  ) {
    this.showDashboardButton = this.router.navigated;
  }

  /**
   * It sets the formTemplateNode to the next node in the linked list, then calls the showFormTemplate()
   * function to display the formTemplate, then calls the saveCurrentLocation() function to save the
   * current location in the linked list, then sets the imageUrl and imageAsDataUrl to undefined
   */
  showNextForm() {
    this.formTemplateNode = this.formTemplateNode.next;
    this.showFormTemplate();
    this.saveCurrentLocation(this.formTemplate.id);
    this.imageUrl = undefined;
    this.imageAsDataUrl = undefined;
  }

  showPreviousForm() {
    this.formTemplateNode = this.formTemplateNode.previous;
    this.showFormTemplate();
    this.saveCurrentLocation(this.formTemplate.id);
  }

  saveCurrentLocation(formTemplateId: string) {
    this.logger.info(`Entering method saveCurrentLocation.
    Parameters: formTemplateId=${formTemplateId}`);

    localStorage.setItem(this.CURRENT_FORM_ID, formTemplateId);
    this.logger.info("Exiting method saveCurrentLocation");
  }
  /**
   * Returns a formTemplate with the id that matches the id passed in as a parameter
   * @param {string} id - The id of the form template you want to get.
   * @returns The formTemplateNode with the id that matches the id passed in.
   */
  getFormTemplateNodeWithId(id: string) {
    this.logger.info(`Entering method getFormTemplateNodeWithId.
    Parameters: id=${id}`);

    for (let index = 0; index < this.formTemplateNodes.length; index++) {
      const formTemplateNode = this.formTemplateNodes[index];
      if (formTemplateNode.formTemplate.id == id) {
        this.logger.info("Exiting method getFormTemplateNodeWithId");
        return formTemplateNode;
      }
    }
  }

  resetCurrentTemplateData() {
    localStorage.removeItem(this.CURRENT_FORM_ID);
  }

  /**
   * Show the last form template that the user was viewing before exiting or refreshing the page
   */
  setCurrentTemplate() {
    let formTemplateId = localStorage.getItem(this.CURRENT_FORM_ID);
    let currentFormTemplateNode =
      this.getFormTemplateNodeWithId(formTemplateId);
    if (currentFormTemplateNode) {
      this.formTemplateNode = currentFormTemplateNode;
      this.showFormTemplate();
    }
  }

  showFormTemplate() {
    this.submitted = false;
    this.finished = false;
    this.formTemplate = this.formTemplateNode.formTemplate;
    this.clientFormGroup = new FormGroup(
      this.getFormValidationObject(this.formTemplateNode.formTemplate)
    );
    this.getFilledOutForm(this.appointmentId, this.formTemplate.id);
    window.scroll(0, 0);
  }

  ngOnInit() {
    const formTemplateId = this.route.snapshot.paramMap.get("formId");
    const serviceIds = this.route.snapshot.paramMap.get("serviceIds");

    this.appointmentId = this.route.snapshot.paramMap.get("appointmentId");
    this.artistId = this.route.snapshot.paramMap.get("artistId");

    if (serviceIds) {
      // Get all the forms that contain the service ids for this appointment
      this.service
        .getAllFormsForServicesFromFirebase(
          serviceIds.split(","),
          this.artistId
        )
        .then((forms) => {
          this.forms = forms;
          if (this.forms && this.forms.length > 0) {
            this.formTemplateNodes = this.createFormsLinkedList(this.forms);

            if (formTemplateId) {
              const node = this.formTemplateNodes.filter((element) => {
                return element.formTemplate.id == formTemplateId;
              })[0];
              this.formTemplateNode = node;
            } else {
              this.formTemplateNode = this.formTemplateNodes[0];
            }

            this.clientFormGroup = new FormGroup(
              this.getFormValidationObject(this.formTemplateNode.formTemplate)
            );
            this.showFormTemplate();
          }
        });
    } else {
      if (formTemplateId) {
        // Get the form template from the server
        this.service.getForm(formTemplateId).subscribe(
          (formTemplate) => {
            this.logger.info(`Retrieved form with ID: ${formTemplateId}`);
            // this.service.log(`Retrieved form with ID: ${formTemplateId}`)
            this.clientFormGroup = new FormGroup(
              this.getFormValidationObject(formTemplate.data())
            );
            this.formTemplateNode.formTemplate = formTemplate.data();
          },
          (err) => {
            this.logger.error(
              `Error retrieving form with ID: ${formTemplateId}`,
              err ? err : ""
            );
            // this.service.log(`Error retrieving form with ID: ${formTemplateId}`, err)
          }
        );
      }

      this.getFilledOutForm(this.appointmentId, formTemplateId);
    }

    this.setCurrentTemplate();

    this.getAllFilledOutFormsForAppointment(this.appointmentId);

    this.filledFormId = this.route.snapshot.paramMap.get("filledFormId");
    this.viewOnly = this.route.snapshot.paramMap.get("viewOnly");

    this.service.getArtist(this.artistId).subscribe(
      (artist) => {
        this.companyName = artist.get("businessName");
        this.arist = artist.data();
        this.fillOutCustomerData();
        this.logger.info("Retrieved the artist from the server successfully");
        // this.service.log("Retrieved the artist from the server successfully")
      },
      (err) => {
        this.logger.error(
          "Error retrieving artist from the server",
          err ? err : ""
        );
        // this.service.log("Error retrieving artist from the server", err)
      }
    );
  }

  getMaxDate(id: String) {
    //NOTE: I am commenting this out because this one method alone can send 10k logs in a matter of seconds.

    // this.logger.info(`Entering method getMaxDate.
    // Parameters: id=${id}`)

    if (id == "todays_date") {
      // this.logger.info('Exiting method getMaxDate')
      return this.todaysDate;
    }

    return "";
  }

  getMinDate(id: String) {
    //NOTE: I am commenting this out because this one method alone can send 10k logs in a matter of seconds.
    // this.logger.info(`Entering method getMinDate.
    // Parameters: id=${id}`)
    if (id == "todays_date") {
      // this.logger.info('Exiting method getMinDate')
      return this.todaysDate;
    }

    return "";
  }

  /**
   * Gets the filled out form from the server and populates the form with the data
   * @param {string} appointmentId - The id of the appointment that you want to retrieve the filled out
   * forms for.
   */
  getAllFilledOutFormsForAppointment(appointmentId: string) {
    this.logger.info(`Entering method getAllFilledOutFormsForAppointment.
    Parameters: appointmentId=${appointmentId}`);
    this.service.getAllFilledFormsForAppointment(appointmentId).subscribe(
      (filledForms) => {
        filledForms.map(
          (filledForm) =>
            (this.filledOutForms[filledForm.data.form_template_id] = true)
        );
        this.logger.info(
          `Retrieved all filled out forms for appointment with id: ${appointmentId}`
        );
        this.logger.info("Exiting method getAllFilledOutFormsForAppointment");
        // this.service.log(`Retrieved all filled out forms for appointment with id: ${appointmentId}`)
      },
      (err) => {
        this.logger.error(
          `Error retrieving filled out forms for appointment with id: ${appointmentId}`,
          err ? err : ""
        );
        // this.service.log(`Error retrieving filled out forms for appointment with id: ${appointmentId}`, err)
      }
    );
  }

  /**
   * Create a linked list with pointers to the next and previous form templates
   * @param forms The form templates to create the linked list from
   */
  createFormsLinkedList(forms: any[]) {
    this.logger.info(`Entering method createFormsLinkedList.
    Parameters: forms=${forms}`);
    let formTemplateNodes: LinkedListNode[] = [];
    let initialNodesArray: LinkedListNode[] = []; //this is a temporary variable to hold the LinkedListNodes before we actually link each node together

    //first let's sort the forms
    forms = forms.sort((firstElement, secondElement) => {
      //for sort function see, Javascript Array.sort documentation
      //This code is assuming we want to sort ascending (e.g. 1, 2, 3 ... N, ... null) NULL is highest -- last
      //handle null cases first
      if (firstElement.order == null && secondElement.order == null) {
        return 0;
      } else if (firstElement.order == null) {
        //null is less than any non-null value, so firstElement should come AFTER secondElement
        return 1;
      } else if (secondElement.order == null) {
        //null is less than any non-null value, so secondElement should come AFTER firstElement
        return -1;
      }
      //now we can safely handle non-null cases
      else if (firstElement.order == secondElement.order) {
        return 0;
      } else if (firstElement.order > secondElement.order) {
        return 1; //secondElement should come before firstElement in ascending order
      } else firstElement.order > secondElement.order;
      {
        return -1; //firstElement should come before secondElement in ascending order
      }
    });

    for (let index = 0; index < forms.length; index++) {
      //foreach form
      const form = forms[index];
      const node: LinkedListNode = {
        //create a new LinkedListNode from this form
        formTemplate: form,
      };

      initialNodesArray.push(node); //store it in our temporary variable
    }

    for (let index = 0; index < initialNodesArray.length; index++) {
      /* Now iterate over the temporary variable to 
                                                                        connect the nodes and store them in the variable 
                                                                        we plan to return */
      const node = initialNodesArray[index];
      node.next =
        index == initialNodesArray.length - 1 //if this is the last node
          ? null //then the next node is null
          : initialNodesArray[index + 1]; //otherwise connect to the next node
      node.previous =
        index == 0 //if this is the first node
          ? null //then previous is null
          : initialNodesArray[index - 1]; //otherwise connect to the previous node
      node.location = index + 1;
      formTemplateNodes.push(node);
    }
    this.logger.info("Exiting method createFormsLinkedList");
    return formTemplateNodes;
  }

  public displayImage(url: string) {
    this.logger.info(`Entering method displayImage.
    Parameters: url=${url}`);
    if (url) {
      this.imageUrl = url;
      this.logger.info("Exiting method displayImage");
    }
  }

  /**
   * Gets the customer document from the server and then fills out the form customer information
   */
  public fillOutCustomerData() {
    this.service.isLoggedIn().then((user) => {
      this.service.getCustomer(user.uid).subscribe(
        (customer) => {
          this.client = customer.info;
          this.logger.info("Customer retrieved from Firestore");
          // this.service.log("Customer retrieved from Firestore")
        },
        (err) => {
          this.logger.error(
            "Error retrieving customer from Firestore",
            err ? err : ""
          );
          // this.service.log("Error retrieving customer from Firestore", err)
        }
      );
    });
  }

  /**
   *
   */
  public getFilledOutForm(appointmentId: string, formTemplateId: string) {
    this.logger.info(`Entering method getFilledOutForm.
    Parameters: appointmentId=${appointmentId}, formTemplateId=${formTemplateId}`);
    this.service
      .getFilledFormByIdForAppointment(formTemplateId, appointmentId)
      .subscribe(
        (filledForm) => {
          this.logger.info(
            `Retrieved the filled form for the appointment: Form Template ID - ${formTemplateId}, Appointment ID - ${appointmentId}`
          );
          // this.service.log(`Retrieved the filled form for the appointment: Form Template ID - ${formTemplateId}, Appointment ID - ${ appointmentId }`)
          if (filledForm.length === 0) {
            this.logger.error("Error: there are no filled forms.");
            return;
          }
          this.formResponse = filledForm[0].data;
          this.displayImage(this.formResponse["client_image"]);
          this.logger.info("Exiting method getFilledOutForm");
        },
        (err) => {
          this.logger.error(
            `Error retrieving the filled form for the appointment: Form Template ID - ${formTemplateId}, Appointment ID - ${appointmentId}`,
            err ? err : ""
          );
          // this.service.log(`Error retrieving the filled form for the appointment: Form Template ID - ${formTemplateId}, Appointment ID - ${ appointmentId }`, err)
        }
      );
  }

  public triggerSnapshot(): void {
    this.trigger.next();
  }

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public handleImage(webcamImage: WebcamImage): void {
    this.logger.info(`Entering method handleImage.
    Parameters: webcamImage=${webcamImage}`);
    // console.info('received webcam image', webcamImage);
    this.logger.info("received webcam image", webcamImage);
    this.webcamImage = webcamImage;
    this.imageAsDataUrl = webcamImage.imageAsDataUrl;
    this.logger.info("Exiting method handleImage");
  }

  public processFile(imageInput: any) {
    this.logger.info(`Entering method processFile.
    Parameters: imageInput=${imageInput}`);
    const file: File = imageInput.files[0];
    const reader = new FileReader();

    if (!this.arist) {
      this.logger.error("Could not find artist. Exiting processFile.");
      return;
    }

    reader.addEventListener("load", (event: any) => {
      let imageAsDataURL = reader.result;
      if (imageAsDataURL) {
        this.imageAsDataUrl = imageAsDataURL;
        this.uploadImage(this.arist, imageAsDataURL);
      }
    });

    if (file) {
      reader.readAsDataURL(file);
      this.logger.info("Exiting method processFile");
    }
  }

  /**
   * The templates contain tags that have the following template - {{tag.name}} - This function replaces these tags with actual content
   * @param data The string to replace the tags for, ex: {{businessName}}
   */
  replaceTags(data: string) {
    //NOTE: I am commenting this out because this one method alone can send 10k logs in a matter of seconds.
    // this.logger.info(`Entering method replaceTags.
    // Parameters: data=${data}`)

    if (!this.arist) {
      this.logger.error("Could not find artist. Exiting replaceTags.");
      return data;
    }

    // Get where the tags start and end within the string
    let startIndex = data.indexOf("{{");
    let endIndex = data.indexOf("}}", startIndex);

    while (startIndex != -1) {
      if (startIndex != -1 && endIndex != -1) {
        let tag = data.substring(startIndex + "{{".length, endIndex);
        let point = tag.indexOf(".");

        // If there's a decimal point in this tag
        if (point != -1) {
          let tags = tag.split(".");
          if (tags[0] === "user") {
            let tagReplacement = this.arist[tags[1]];
            data = data.replace(tag, tagReplacement);
          }
        } else {
          data = data.replace(tag, this.formTemplateNode.formTemplate[tag]);
        }
      }

      startIndex = data.indexOf("{{", endIndex);
      endIndex = data.indexOf("}}", startIndex);
    }

    data = data.replace("{{", "");
    data = data.replace("}}", "");
    // this.logger.info('Exiting method replaceTags')
    return data;
  }

  // Create a validation object necessary to apply validators to the form
  getFormValidationObject(form: any) {
    this.logger.info(`Entering method getFormValidationObject.
    Parameters: form=${form}`);
    var validationObject = {};
    form.sections.forEach((section) => {
      section.data.forEach((input) => {
        if (input.type != FormInputTypes.IMAGE) {
          if (input.type == FormInputTypes.CHECKBOX) {
            validationObject[input.id] = new FormControl(
              false,
              input.required == true ? [Validators.requiredTrue] : []
            );
          } else {
            validationObject[input.id] = new FormControl(
              "",
              input.required == true ? [Validators.required] : []
            );
          }
        }
      });
    });
    this.logger.info("Exiting method getFormValidationObject");

    return validationObject;
  }

  getControl(name: string) {
    // this.logger.info(`Entering method getControl.
    // Parameters: name=${name}`)
    // this.logger.info('Exiting method getControl')
    return this.clientFormGroup.get(name);
  }

  /**
   * User presses the submit button
   */
  onSubmit() {
    this.submitted = true;

    if (this.clientFormGroup.invalid) {
      this.formError = "There are errors up above";
      this.logger.error("User has errors in form.", {
        appointmentId: this.appointmentId,
        filledFormId: this.filledFormId,
        formResponse: this.formResponse,
      });
      setInterval(() => {
        this.formError = "";
      }, 6000);
      return;
    }

    // If the camera is displayed meaning that an image is required for this form than get the image data
    // and if the user has not selected an image we can't allow them to submit the form
    if (document.getElementById("camera")) {
      if (this.webcamImage == null) {
        this.formError = "Please add an image";
        this.logger.error("Image has not been added to form.", {
          appointmentId: this.appointmentId,
          filledFormId: this.filledFormId,
          formResponse: this.formResponse,
        });
        setInterval(() => {
          this.formError = "";
        }, 6000);
        return;
      }
    }

    // If the form is valid then send the form data to the server
    this.logger.info("Form submitted!", {
      appointmentId: this.appointmentId,
      filledFormId: this.filledFormId,
      formResponse: this.formResponse,
    });
    this.submitForm();
  }

  handleCameraInitError(event) {
    // At the moment the reason why there's a failure is irrelevant, however, it's almost 100% because the device does not have a camera, and if that's the case we need to
    // allow the user to get the image from their device
    this.logger.info(`Entering method handleCameraInitError.
    Parameters: event=${event}`);
    console.log(event);
    this.cameraNotAvailable = true;
    this.logger.info("Exiting method handleCameraInitError");
  }

  /**
   * Upload an image to the server
   * @param user
   */
  async uploadImage(user, image) {
    this.logger.info(`Entering method uploadImage.
    Parameters: user=${user}, image=${image}`);

    // Create a reference to where we're going to save the client's image
    var storageRef = firebase.storage().ref();
    var imageRef = storageRef.child("images/" + user.uid + "/" + Guid.create());
    const blob = await (await fetch(image)).blob();

    // Save the clients image and get the download url
    let snapshot = await imageRef.put(blob);
    console.log(snapshot);
    const downloadUrl = await snapshot.ref.getDownloadURL();
    this.formResponse["client_image"] = downloadUrl;

    this.logger.info("Exiting method uploadImage");
    return;
  }

  /**
   * Send all the necessary form information to the server
   */
  async submitForm() {
    this.saving = true;

    // Make sure the user is logged in and if so, continue, otherwise take the user to the login screen
    let user = await this.service.isLoggedIn();

    if (!user) {
      this.formError = "You must be logged in";
      this.saving = false;
      return;
    }

    let formId = Guid.create().toString();
    this.formResponse["client_id"] = user.uid;

    if (this.imageAsDataUrl != null) {
      await this.uploadImage(user, this.imageAsDataUrl);
    }

    // Delete any keys that have no value
    for (let key in this.formResponse) {
      if (this.formResponse[key] == undefined) {
        delete this.formResponse[key];
      }
    }

    for (let key in this.client) {
      if (this.client[key] == undefined) {
        delete this.client[key];
      }
    }

    this.formResponse["form_template_id"] =
      this.formTemplateNode.formTemplate.id;
    this.formResponse["appointment_id"] = this.appointmentId;

    try {
      await this.service.createFilledForm(
        this.formResponse,
        this.filledFormId != null ? this.filledFormId : formId
      );

      this.logger.info("Filled form created successfully");
      // this.service.log("Filled form created successfully")

      // If we have not already added this client to the artist's list of clients than do so now
      await this.service.updateUser(this.artistId, user.uid);

      // After we've updated the user we need to create a document that contains a relationship between the artist and the client
      await this.service.createRelationship(this.artistId, user.uid);

      this.logger.info("User updated successfully");
      // this.service.log("User updated successfully.")

      this.saving = false;
      this.finished = true;

      this.filledOutForms[this.formTemplateNode.formTemplate.id] = true;

      if (
        Object.keys(this.filledOutForms).length == this.formTemplateNodes.length
      ) {
        this.finishedAllForms = true;
        this.resetCurrentTemplateData();

        // The allFormsSigned key is used within the iOS and Android app
        this.service
          .updateAppointment(this.appointmentId, {
            allFormsCompleted: true,
          })
          .then(() => {
            this.logger.info(
              `Set allFormsCompleted for appointment with id to true: ${this.appointmentId}`
            );
            // this.service.log(`Set allFormsCompleted for appointment with id to true: ${ this.appointmentId }`)
          })
          .catch((err) => {
            this.logger.error(
              `Error setting allFormsCompleted key to true for appointment with id: ${this.appointmentId}`,
              err ? err : ""
            );
            // this.service.log(`Error setting allFormsCompleted key to true for appointment with id: ${ this.appointmentId }`)
          });
      }
    } catch (err) {
      this.logger.error("Error submitting a form", err ? err : "");
      // this.service.log("Error submitting a form", err)
    }
  }

  backToDashboardClicked() {
    this.location.back();
  }
}
